import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';
import { useTheme } from '../context/ThemeContext';
import './ChallengeDetail.css';

export default function ChallengeDetail() {
  const { id } = useParams<{ id: string }>();
  const { colors } = useTheme();
  const [challenge, setChallenge] = useState<any>(null);
  const [progress, setProgress] = useState<any>(null);
  const [taskMap, setTaskMap] = useState<Record<string, { id: string; title: string }>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    loadChallenge();
    loadProgress();
  }, [id]);

  const loadChallenge = async () => {
    if (!id) return;
    
    try {
      const docRef = doc(db, 'challenges', id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setChallenge({ id: docSnap.id, ...docSnap.data() });
      }
    } catch (error) {
      console.error('Error loading challenge:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProgress = async () => {
    if (!id || !auth.currentUser) return;

    try {
      const progressId = `${id}_${auth.currentUser.uid}`;
      const progressRef = doc(db, 'userChallengeProgress', progressId);
      const progressSnap = await getDoc(progressRef);
      
      if (progressSnap.exists()) {
        setProgress(progressSnap.data());
      } else {
        // Set default progress
        setProgress({
          count: 0,
          requiredCount: challenge?.requiredCount || 0,
          status: 'in_progress'
        });
      }
    } catch (error) {
      console.error('Error loading progress:', error);
    }
  };

  useEffect(() => {
    if (challenge) {
      loadProgress();
      loadTaskDetails();
    }
  }, [challenge]);

  const loadTaskDetails = async () => {
    if (!challenge?.eligible?.taskIds || challenge.eligible.taskIds.length === 0) {
      setTaskMap({});
      return;
    }

    try {
      const map: Record<string, { id: string; title: string }> = {};
      await Promise.all(
        challenge.eligible.taskIds.map(async (taskId: string) => {
          try {
            const taskRef = doc(db, 'tasks', taskId);
            const taskSnap = await getDoc(taskRef);
            if (taskSnap.exists()) {
              const data = taskSnap.data();
              map[taskId] = {
                id: taskId,
                title: data.title || 'Untitled Task',
              };
            } else {
              map[taskId] = {
                id: taskId,
                title: `Task ${taskId}`,
              };
            }
          } catch (taskError) {
            console.error('Error loading task', taskId, taskError);
            map[taskId] = {
              id: taskId,
              title: `Task ${taskId}`,
            };
          }
        })
      );
      setTaskMap(map);
    } catch (error) {
      console.error('Error loading task details:', error);
    }
  };

  const formatDate = (timestamp: any): string => {
    if (!timestamp) return '';
    const ms = (timestamp && typeof timestamp === 'object' && 'toMillis' in timestamp)
      ? timestamp.toMillis()
      : (typeof timestamp === 'number' ? timestamp : 0);
    if (ms === 0) return '';
    const date = new Date(ms);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="challenge-detail-container" style={{ background: colors.background, color: colors.text }}>
        <div className="loading">Loading challenge...</div>
      </div>
    );
  }

  if (!challenge) {
    return (
      <div className="challenge-detail-container" style={{ background: colors.background, color: colors.text }}>
        <div className="error">Challenge not found</div>
        <Link to="/dashboard" style={{ color: colors.primary }}>Back to Dashboard</Link>
      </div>
    );
  }

  const count = progress?.count || 0;
  const required = progress?.requiredCount || challenge.requiredCount;
  const completedTaskIds: string[] = progress?.completedTaskIds || [];
  const progressPercent = Math.min(100, required ? (count / required) * 100 : 0);
  const isCompleted = progress?.status === 'rewarded' || count >= required;

  return (
    <div className="challenge-detail-container" style={{ background: colors.background, color: colors.text }}>
      <div className="challenge-detail-content" style={{ background: colors.card, borderColor: colors.border }}>
        <Link to="/dashboard" style={{ color: colors.primary, marginBottom: '20px', display: 'inline-block' }}>
          ← Back to Dashboard
        </Link>

        <div className="challenge-header-detail">
          {challenge.imageUrl ? (
            <img 
              src={challenge.imageUrl} 
              alt={challenge.title}
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '40px',
                objectFit: 'cover',
                marginBottom: '20px'
              }}
            />
          ) : (
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '40px',
              background: colors.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '40px',
              marginBottom: '20px'
            }}>
              🎯
            </div>
          )}
          <h1 style={{ color: colors.text, margin: '0 0 10px 0' }}>{challenge.title}</h1>
        </div>

        <div className="challenge-progress-section" style={{ background: colors.background, borderColor: colors.border }}>
          <h2 style={{ color: colors.text }}>Your Progress</h2>
          <div style={{ marginBottom: '16px' }}>
            <div style={{ 
              height: '24px', 
              borderRadius: '12px', 
              background: colors.border, 
              overflow: 'hidden',
              marginBottom: '8px'
            }}>
              <div style={{
                width: `${progressPercent}%`,
                height: '100%',
                background: colors.primary,
                transition: 'width 0.3s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: '12px',
                fontWeight: 'bold'
              }}>
                {progressPercent > 10 ? `${Math.round(progressPercent)}%` : ''}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: colors.secondary }}>
              <span>{count} / {required} completed</span>
              <span>{isCompleted ? '✓ Completed' : 'In progress'}</span>
            </div>
          </div>
        </div>

        <div className="challenge-info-section" style={{ background: colors.background, borderColor: colors.border }}>
          <h2 style={{ color: colors.text }}>Challenge Details</h2>
          <div className="info-row">
            <span style={{ color: colors.secondary }}>Reward:</span>
            <span style={{ color: colors.primary, fontWeight: 'bold' }}>+{challenge.pixReward} PIX</span>
          </div>
          <div className="info-row">
            <span style={{ color: colors.secondary }}>Required:</span>
            <span style={{ color: colors.text }}>{challenge.requiredCount} submissions</span>
          </div>
          <div className="info-row">
            <span style={{ color: colors.secondary }}>Starts:</span>
            <span style={{ color: colors.text }}>{formatDate(challenge.startAt)}</span>
          </div>
          <div className="info-row">
            <span style={{ color: colors.secondary }}>Ends:</span>
            <span style={{ color: colors.text }}>{formatDate(challenge.endAt)}</span>
          </div>
        </div>

        {challenge.eligible && (
          <div className="challenge-eligible-section" style={{ background: colors.background, borderColor: colors.border }}>
            <h2 style={{ color: colors.text }}>Eligible Tasks</h2>
            {challenge.eligible.categories && challenge.eligible.categories.length > 0 && (
              <div>
                <strong style={{ color: colors.secondary }}>Categories:</strong>
                <div style={{ marginTop: '8px' }}>
                  {challenge.eligible.categories.map((cat: string, idx: number) => (
                    <span key={idx} style={{
                      display: 'inline-block',
                      padding: '4px 12px',
                      margin: '4px',
                      borderRadius: '12px',
                      background: colors.primary + '20',
                      color: colors.primary,
                      fontSize: '12px'
                    }}>
                      {cat}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {challenge.eligible.subcategories && challenge.eligible.subcategories.length > 0 && (
              <div style={{ marginTop: '12px' }}>
                <strong style={{ color: colors.secondary }}>Subcategories:</strong>
                <div style={{ marginTop: '8px' }}>
                  {challenge.eligible.subcategories.map((sub: string, idx: number) => (
                    <span key={idx} style={{
                      display: 'inline-block',
                      padding: '4px 12px',
                      margin: '4px',
                      borderRadius: '12px',
                      background: colors.primary + '20',
                      color: colors.primary,
                      fontSize: '12px'
                    }}>
                      {sub}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {challenge.eligible?.taskIds?.length > 0 && (
          <div className="challenge-tasks-section" style={{ background: colors.background, borderColor: colors.border }}>
            <h2 style={{ color: colors.text }}>Tasks To Complete</h2>
            <p style={{ color: colors.secondary, marginTop: 0 }}>
              Check off the tasks below as you finish them. Completed tasks are synced from your submissions.
            </p>
            <div className="task-list">
              {challenge.eligible.taskIds.map((taskId: string) => {
                const task = taskMap[taskId];
                const isTaskCompleted = completedTaskIds.includes(taskId);
                return (
                  <label
                    key={taskId}
                    className="task-checkbox-row"
                    style={{
                      borderColor: colors.border,
                      background: colors.card
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isTaskCompleted}
                      readOnly
                    />
                    <span style={{ color: colors.text }}>
                      {task?.title || (Object.keys(taskMap).length > 0 ? `Task ${taskId}` : 'Loading task...')}
                    </span>
                    {isTaskCompleted && (
                      <span className="task-status" style={{ color: colors.primary }}>
                        Completed
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>
        )}

        <Link 
          to="/dashboard"
          className="challenge-back-button"
          style={{
            background: colors.primary,
            color: '#fff',
            display: 'inline-block',
            padding: '12px 24px',
            borderRadius: '8px',
            textDecoration: 'none',
            fontWeight: '600',
            marginTop: '20px'
          }}
        >
          Back to Dashboard
        </Link>
      </div>
    </div>
  );
}

